// The invitation's two reads and one act, for the landing page (#415).
//
// `GET /invites/preview?token=` needs no session: the person holding the link
// may have no account yet, and what it answers is what the link opens — the
// workspace, the address it was sent to, the role, and whether that address
// already has an account, which is how the page knows whether to offer a
// registration or a sign-in (`routes/invites.py::preview`).
//
// **And the invitee's own read, which needs no link at all** (#440):
// `GET /invites/waiting` answers the invitations addressed to the signed-in
// user's *verified* address, across every context, and its accept takes the
// invitation's id. Not to be confused with `GET /invites` in `customers.ts`,
// which is a workspace's view of who it has asked in and needs `members:list`
// inside that workspace — the route that answered 403 to a signed-in invitee on
// 2026-09-12, which is the defect this closes.

import type { Api } from "./http";

export const INVITE_PREVIEW_PATH = "/invites/preview";
export const INVITE_ACCEPT_PATH = "/invites/accept";

export interface InvitePreview {
  workspace_id: string;
  workspace_name: string;
  email: string;
  role: string;
  expires_at: string;
  account_exists: boolean;
}

export const previewInvite = (api: Api, token: string): Promise<InvitePreview> =>
  api.get<InvitePreview>(`${INVITE_PREVIEW_PATH}?token=${encodeURIComponent(token)}`);

/** A signed-in user accepts and gains the membership; the context is selected after. */
export const acceptInvite = (
  api: Api,
  token: string,
): Promise<{ workspace_id: string; role: string }> =>
  api.post<{ workspace_id: string; role: string }>(INVITE_ACCEPT_PATH, { token });

export const INVITES_WAITING_PATH = "/invites/waiting";

/**
 * One invitation waiting for the signed-in user (`invites.py::WaitingInvitation`).
 *
 * Deliberately the shape of a `Membership` plus a deadline and an inviter, because
 * the two are rendered by the same row on the same screen: the workspaces you are
 * in, and the ones asking you in. **There is no token on it** — the `id` is what
 * the accept takes.
 */
export interface WaitingInvitation {
  id: string;
  kind: "workspace" | "org";
  workspace_id: string | null;
  org_id: string | null;
  name: string | null;
  org_name: string | null;
  org_kind: string | null;
  role: string;
  invited_by: string;
  expires_at: string;
}

/** Answers `[]` for an unverified address rather than refusing (`routes/invites.py`). */
export const listWaitingInvitations = (api: Api): Promise<WaitingInvitation[]> =>
  api.get<WaitingInvitation[]>(INVITES_WAITING_PATH);

/** Accept by id, with no token in the request; the context is selected after. */
export const acceptWaitingInvitation = (
  api: Api,
  inviteId: string,
): Promise<{ workspace_id: string | null; org_id: string | null; role: string }> =>
  api.post(`${INVITES_WAITING_PATH}/${encodeURIComponent(inviteId)}/accept`);
